import React, { useState, useEffect, useRef, memo } from 'react';
import { TrendingUp, TrendingDown, Activity, Hash, Layers, ArrowUpRight, ArrowDownRight, Minus, Globe } from 'lucide-react';
import { cn } from '../lib/utils';
import { Card } from './ui/card';
import { motion } from 'framer-motion';

import { useDashboardData } from '../hooks/useDashboardData';
import { useSettingsStore } from '../stores/settingsStore';
import { useIndexIndicators } from '../hooks/useIndexIndicators';
import { useGiftNifty } from '../hooks/useGiftNifty';
import * as CONFIG from '../config/constants';
import { useMarketStore } from '../services/store';
import { findSnapshotAtTime, extractSnapshotOptions, extractSpotPrice, timeToMin } from '../lib/snapshot-search';
import { GeometricAccordion } from './ui/GeometricAccordion';

// ---------------------------------------------------------
// User Provided Types & Components (Tweaked for dynamic data)
// ---------------------------------------------------------

export interface MarketData {
  name: string;
  currentValue: number;
  diff: number;
  giftNifty: number;
  dayLow: number;
  dayHigh: number;
  dayOpen: number;
  idx5mLow: number;
  openT1: number;
  openT1Type: string;
  openT2: number;
  openT2Type: string;
  liveT1: number;
  liveT1Type: string;
  liveT2: number;
  liveT2Type: string;
}

type PillColor = 'green' | 'red' | 'blue' | 'black' | 'gray';

interface BottomPill {
  text: string;
  color: PillColor;
}

interface TopPill {
  text: string | number;
  color: PillColor;
  large?: boolean;
  glowing?: boolean;
  dottedLine?: boolean;
}

interface MarkerData {
  value: number;
  topPills: TopPill[];
  bottomPills: BottomPill[];
  dotTypes: ('solid' | 'ring' | 'tiny' | 'glow')[];
  dotColors: PillColor[];
}

const formatIndianNumber = (num: number, hideDecimals?: boolean) => {
  if (isNaN(num)) return num.toString();
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: hideDecimals ? 0 : 2,
    maximumFractionDigits: hideDecimals ? 0 : 2,
  }).format(num);
};

function getStyles(color: PillColor) {
  switch (color) {
    case 'green': return { text: 'text-teal-700', bg: 'bg-teal-50', border: 'border-teal-200', solid: 'bg-teal-500', glow: 'shadow-[0_0_15px_rgba(20,184,166,0.3)]' };
    case 'red': return { text: 'text-rose-700', bg: 'bg-rose-50', border: 'border-rose-200', solid: 'bg-rose-500', glow: 'shadow-[0_0_15px_rgba(244,63,94,0.3)]' };
    case 'blue': return { text: 'text-sky-700', bg: 'bg-sky-50', border: 'border-sky-200', solid: 'bg-sky-500', glow: 'shadow-[0_0_15px_rgba(14,165,233,0.3)]' };
    case 'black': return { text: 'text-slate-900', bg: 'bg-slate-100', border: 'border-slate-300', solid: 'bg-slate-900', glow: 'shadow-[0_0_15px_rgba(15,23,42,0.2)]' };
    case 'gray': return { text: 'text-slate-600', bg: 'bg-slate-100', border: 'border-slate-200', solid: 'bg-slate-400', glow: '' };
  }
}

const getMarkers = (data: MarketData) => {
  const map = new Map<number, MarkerData>();
  
  const add = (
    value: number, 
    bottom?: BottomPill, 
    top?: TopPill, 
    dotType: 'solid'|'ring'|'tiny'|'glow' = 'tiny', 
    dotColor: PillColor = 'gray'
  ) => {
    if (value <= 0) return; // ignore missing values
    if (!map.has(value)) {
      map.set(value, { value, topPills: [], bottomPills: [], dotTypes: [], dotColors: [] });
    }
    const m = map.get(value)!;
    if (bottom && !m.bottomPills.find(p => p.text === bottom.text)) m.bottomPills.push(bottom);
    if (top && !m.topPills.find(p => p.text === top.text)) m.topPills.push(top);
    m.dotTypes.push(dotType);
    m.dotColors.push(dotColor);
  };

  // Add lower priority first
  if (data.idx5mLow > 0) add(data.idx5mLow, { text: 'IDX 5M LOW', color: 'red' }, { text: formatIndianNumber(data.idx5mLow, true), color: 'red' }, 'tiny', 'gray');
  if (data.dayLow > 0) add(data.dayLow, { text: 'DAY LOW', color: 'green' }, { text: formatIndianNumber(data.dayLow, true), color: 'green' }, 'tiny', 'gray');
  if (data.dayOpen > 0) add(data.dayOpen, { text: 'DAY OPEN', color: 'blue' }, { text: formatIndianNumber(data.dayOpen, true), color: 'blue' }, 'tiny', 'gray');
  if (data.dayHigh > 0) add(data.dayHigh, { text: 'DAY HIGH', color: 'red' }, { text: formatIndianNumber(data.dayHigh, true), color: 'red' }, 'tiny', 'gray');
  
  // Zones
  if (data.openT2 > 0) add(data.openT2, { text: '09:16 T2', color: 'green' }, { text: formatIndianNumber(data.openT2, true), color: 'green', large: true, glowing: true }, 'glow', 'green');
  if (data.openT1 > 0) add(data.openT1, { text: '09:16 T1', color: 'red' }, { text: formatIndianNumber(data.openT1, true), color: 'red', large: true, glowing: true }, 'glow', 'red');
  
  // Live levels
  if (data.liveT1 > 0) add(data.liveT1, { text: 'LIVE T1', color: 'green' });
  if (data.liveT2 > 0) add(data.liveT2, { text: 'LIVE T2', color: 'green' });
  
  // Current Value
  if (data.currentValue > 0) add(data.currentValue, undefined, { text: formatIndianNumber(data.currentValue), color: 'black', large: true, glowing: true, dottedLine: true }, 'solid', 'black');

  return Array.from(map.values()).sort((a, b) => a.value - b.value);
};

const Marker: React.FC<{ marker: MarkerData, getPercent: (v: number) => number }> = ({ marker, getPercent }) => {
  const left = getPercent(marker.value);
  const hasDottedLine = marker.topPills.some(p => p.dottedLine);
  
  return (
    <div className="absolute top-1/2 -translate-y-1/2 z-20 flex flex-col items-center" style={{ left: `${left}%` }}>
      
      {/* Top Pills Area */}
      {marker.topPills.length > 0 && (
        <div className={cn(
          "absolute left-1/2 -translate-x-1/2 flex space-x-1.5 items-end",
          hasDottedLine ? "bottom-[24px]" : "bottom-[24px]"
        )}>
          {marker.topPills.map((pill, i) => (
            <div key={i} className="flex flex-col items-center group">
              <div className={cn(
                 "border font-mono font-medium rounded-[4px] whitespace-nowrap text-center relative z-10 transition-all duration-300 backdrop-blur-md",
                 getStyles(pill.color).text,
                 getStyles(pill.color).border,
                 getStyles(pill.color).bg,
                 pill.large ? "text-xs px-2.5 py-1" : "text-[10px] px-1.5 py-0.5 opacity-80",
                 pill.glowing ? getStyles(pill.color).glow : ""
              )}>
                 {pill.text}
              </div>
              {pill.dottedLine ? (
                <div className="absolute top-full left-1/2 -translate-x-1/2 h-[32px] w-px border-l border-dashed border-slate-300 mt-1 opacity-100" />
              ) : (
                <div className="absolute top-full left-1/2 -translate-x-1/2 h-[16px] w-px border-l border-solid border-slate-300 mt-1 opacity-0 group-hover:opacity-100 transition-opacity" />
              )}
            </div>
          ))}
        </div>
      )}
      
      {/* Dot Area */}
      <div className="flex space-x-0.5 items-center justify-center relative z-20 h-4">
        {marker.dotTypes.map((type, i) => {
           const color = marker.dotColors[i] || 'gray';
           const styles = getStyles(color);
           
           if (type === 'glow') {
             return (
                <div key={i} className="relative flex items-center justify-center">
                   <div className={cn("absolute w-4 h-4 rounded-full opacity-40 animate-pulse", styles.solid, styles.glow)} />
                   <div className={cn("w-2 h-2 rounded-full relative z-10", styles.solid)} />
                </div>
             );
           }
           if (type === 'solid') {
             return <div key={i} className={cn("w-2 h-2 rounded-full ring-2 ring-white", styles.solid, styles.glow)} />;
           }
           if (type === 'ring') {
             return <div key={i} className={cn("w-2.5 h-2.5 rounded-full border-[2px] bg-white", styles.border)} />;
           }
           return <div key={i} className="w-1 h-3 rounded-full bg-slate-300" />;
        })}
      </div>

      {/* Bottom Pills Area */}
      {marker.bottomPills.length > 0 && (
        <div className="absolute top-[28px] left-1/2 -translate-x-1/2 flex space-x-1 z-10">
          {marker.bottomPills.map((pill, i) => (
            <span key={i} className={cn(
              "px-1.5 py-0.5 font-sans font-semibold rounded-[4px] whitespace-nowrap text-[8.5px] tracking-[0.15em] uppercase backdrop-blur-md border",
              getStyles(pill.color).bg,
              getStyles(pill.color).text,
              getStyles(pill.color).border,
            )}>
              {pill.text}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

export const MarketGauge: React.FC<{ data: MarketData }> = ({ data }) => {
  const [trend, setTrend] = useState<'up' | 'down' | 'none'>('none');
  const prevValueRef = useRef(data.currentValue);

  useEffect(() => {
    if (data.currentValue > prevValueRef.current) {
      setTrend('up');
    } else if (data.currentValue < prevValueRef.current) {
      setTrend('down');
    }
    prevValueRef.current = data.currentValue;
  }, [data.currentValue]);

  const markers = getMarkers(data);
  const values = markers.map(m => m.value);
  const dataMin = values.length > 0 ? Math.min(...values) : 0;
  const dataMax = values.length > 0 ? Math.max(...values) : 0;
  
  const rawRange = dataMax - dataMin;
  const range = rawRange === 0 ? 100 : rawRange;
  
  const minVal = dataMin - range * 0.15;
  const maxVal = dataMax + range * 0.15;
  const totalRange = maxVal - minVal;

  const getPercent = (val: number) => {
    if (totalRange === 0) return 50;
    return ((val - minVal) / totalRange) * 100;
  };

  const pMin = getPercent(dataMin);
  const pDayLow = data.dayLow > 0 ? getPercent(data.dayLow) : pMin;
  const pDayHigh = data.dayHigh > 0 ? getPercent(data.dayHigh) : pMin;
  const pMax = getPercent(dataMax);
  
  const isPositive = data.diff >= 0;

  return (
    <Card className="w-full mx-auto bg-white/90 backdrop-blur-xl border-slate-200 shadow-xl overflow-hidden rounded-[24px] ring-1 ring-slate-900/5 mb-2">
      
      {/* Top Section - Dashboard Overview */}
      <div className="p-4 md:p-6 pb-20 md:pb-24 flex flex-col md:flex-row flex-nowrap justify-between items-start gap-4 md:gap-4 lg:gap-6 bg-gradient-to-br from-slate-100 to-slate-50 relative overflow-hidden">
        
        {/* Glow overlay */}
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-teal-100 rounded-full blur-[100px] -translate-x-1/2 -translate-y-1/2 pointer-events-none" />

        {/* Main Price Block */}
        <div className="space-y-4 relative z-10 w-full md:w-auto">
          <div className="flex items-center space-x-4">
             <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-white border border-slate-200 text-teal-600 shadow-sm backdrop-blur-md">
                <Activity className="w-5 h-5" />
             </div>
             <h2 className="text-sm font-heading font-bold tracking-[0.2em] text-slate-500 uppercase flex items-center gap-3">
                {data.name} 
                <div className="flex space-x-1">
                  <span className="w-1 h-1 rounded-full bg-teal-500 animate-ping" />
                  <span className="w-1 h-1 rounded-full bg-teal-500 animate-pulse delay-75" />
                </div>
             </h2>
          </div>
          
          <div className="flex flex-col lg:flex-row lg:items-end gap-3 lg:gap-6">
            <div className="flex items-center gap-3">
              <span className="text-4xl font-black tracking-tighter text-slate-900 font-mono drop-shadow-sm leading-none">
                {formatIndianNumber(data.currentValue)}
              </span>
              <div className="flex flex-col justify-center">
                 {trend === 'up' && <ArrowUpRight className="w-5 h-5 md:w-6 md:h-6 text-teal-500 animate-pulse" />}
                 {trend === 'down' && <ArrowDownRight className="w-5 h-5 md:w-6 md:h-6 text-rose-500 animate-pulse" />}
                 {trend === 'none' && <Minus className="w-5 h-5 md:w-6 md:h-6 text-slate-300" />}
              </div>
            </div>
            <div className="flex items-center gap-2 sm:mb-1">
              <div className={cn(
                "flex items-center space-x-1.5 px-3 py-1.5 rounded-xl text-sm font-mono font-bold border w-fit shadow-sm transition-all duration-300 hover:shadow-md hover:-translate-y-0.5",
                isPositive ? "bg-teal-50 text-teal-700 border-teal-200" : "bg-rose-50 text-rose-700 border-rose-200"
              )}>
                {isPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                <span>{isPositive ? '+' : ''}{formatIndianNumber(data.diff)}</span>
              </div>
              {data.currentValue > 0 && (
                <div className={cn(
                  "flex items-center px-2.5 py-1.5 rounded-xl text-xs tracking-wider font-black font-mono border shadow-sm backdrop-blur-sm transition-all duration-300 hover:shadow-md hover:-translate-y-0.5",
                  isPositive ? "bg-teal-50/70 text-teal-600 border-teal-200" : "bg-rose-50/70 text-rose-600 border-rose-200"
                )}>
                  {isPositive ? '+' : ''}{((data.diff / (data.currentValue - data.diff)) * 100).toFixed(2)}%
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Secondary Metrics */}
        <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto relative z-10 sm:mt-6">
           {data.name === 'NIFTY' && (
             <div className="relative flex flex-col w-full sm:w-auto mt-6 sm:mt-0">
               <div className="absolute -top-7 left-1/2 -translate-x-1/2 sm:left-auto sm:right-4 sm:translate-x-0 flex justify-center sm:justify-end z-20">
                 {(() => {
                   if (data.giftNifty <= 0 || data.currentValue <= 0) return null;
                   const diff = data.giftNifty - data.currentValue;
                   const isPositive = diff >= 0;
                   return (
                     <div className={cn(
                       "px-2.5 py-0.5 rounded-full text-[10px] font-bold font-mono tracking-wider border whitespace-nowrap shadow-sm backdrop-blur-md flex items-center gap-1 transition-all duration-300 hover:scale-105",
                       isPositive ? "bg-teal-50/90 text-teal-700 border-teal-300" : "bg-rose-50/90 text-rose-700 border-rose-300"
                     )}>
                       {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                       {isPositive ? '+' : ''}{formatIndianNumber(diff)}
                     </div>
                   )
                 })()}
               </div>
               <MetricBox label="Gift Nifty" value={formatIndianNumber(data.giftNifty)} icon={<Hash className="w-4 h-4" />} color="blue" />
             </div>
           )}
           <div className="flex flex-col w-full sm:w-auto">
             <MetricBox label="Day Range" value={`${formatIndianNumber(data.dayLow, true)} - ${formatIndianNumber(data.dayHigh, true)}`} icon={<Layers className="w-4 h-4" />} color="slate" />
           </div>
        </div>
      </div>

      {/* Main Gauge Stage */}
      <div className="p-4 md:p-6 pt-10 md:pt-14 pb-6 relative bg-white/90 backdrop-blur-3xl -mt-12 md:-mt-16 rounded-t-[24px] md:rounded-t-[32px] shadow-[0_-20px_40px_-10px_rgba(0,0,0,0.05)] border-t border-white z-20">
        
        {/* Background Grids & FX */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.03]" 
             style={{ backgroundImage: 'linear-gradient(to right, #000 1px, transparent 1px), linear-gradient(to bottom, #000 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
        
        {/* The Track */}
        <div className="relative mt-12 mb-16 h-2 w-full max-w-[95%] mx-auto">
          {/* Base Line */}
          <div className="absolute inset-0 bg-slate-100 rounded-full shadow-[inset_0_1px_3px_rgba(0,0,0,0.05)]" />
          
          {/* Colored Zones */}
          <div className="absolute inset-0 rounded-full overflow-hidden">
            {/* Left Red Zone (Min to Low) */}
            <motion.div 
              initial={{ width: 0 }}
              animate={{ left: `${pMin}%`, width: `${Math.max(0, pDayLow - pMin)}%` }}
              transition={{ duration: 1.5, ease: "easeOut" }}
              className="absolute top-0 h-full bg-rose-500 shadow-[0_0_15px_rgba(244,63,94,0.5)]"
            />
            {/* Green Zone (Low to High) */}
            <motion.div 
              initial={{ width: 0 }}
              animate={{ left: `${pDayLow}%`, width: `${Math.max(0, pDayHigh - pDayLow)}%` }}
              transition={{ duration: 1.5, ease: "easeOut", delay: 0.1 }}
              className="absolute top-0 h-full bg-teal-500 shadow-[0_0_15px_rgba(45,212,191,0.5)]"
            />
            {/* Right Red Zone (High to Max) */}
            <motion.div 
              initial={{ width: 0 }}
              animate={{ left: `${pDayHigh}%`, width: `${Math.max(0, pMax - pDayHigh)}%` }}
              transition={{ duration: 1.5, ease: "easeOut", delay: 0.2 }}
              className="absolute top-0 h-full bg-rose-500 shadow-[0_0_15px_rgba(244,63,94,0.5)]"
            />
          </div>

          {/* Markers */}
          <div className="absolute inset-0">
             {markers.map(m => <Marker key={m.value} marker={m} getPercent={getPercent} />)}
          </div>
        </div>
        
        {/* Bento Grid Info Below */}
        <div className="mt-8 w-full mx-auto grid grid-cols-2 md:grid-cols-5 gap-3 relative z-10">
           <BentoCell label="09:16 T1" value={formatIndianNumber(data.openT1, true)} suffix={data.openT1Type} color="red" />
           <BentoCell label="09:16 T2" value={formatIndianNumber(data.openT2, true)} suffix={data.openT2Type} color="green" />
           <BentoCell label="5M LOW" value={data.idx5mLow ? formatIndianNumber(data.idx5mLow, true) : '—'} color="gray" />
           <BentoCell label="LIVE T1" value={formatIndianNumber(data.liveT1, true)} suffix={data.liveT1Type} color="green" />
           <BentoCell label="LIVE T2" value={formatIndianNumber(data.liveT2, true)} suffix={data.liveT2Type} color="green" />
        </div>
      </div>
      
    </Card>
  );
};

// Sub-components
const MetricBox = ({ label, value, icon, color }: { label: string, value: string, icon: React.ReactNode, color: 'blue' | 'slate' }) => {
  const isBlue = color === 'blue';
  return (
    <div className={cn(
      "flex flex-col justify-between p-4 md:p-5 rounded-2xl border flex-1 min-w-[140px] md:min-w-[160px] lg:min-w-[200px] relative overflow-hidden group transition-all duration-300",
      isBlue 
        ? "bg-white border-sky-100 shadow-[0_4px_20px_-4px_rgba(14,165,233,0.1)] hover:shadow-[0_8px_30px_-6px_rgba(14,165,233,0.15)] hover:border-sky-200" 
        : "bg-white border-slate-100 shadow-[0_4px_20px_-4px_rgba(15,23,42,0.05)] hover:shadow-[0_8px_30px_-6px_rgba(15,23,42,0.08)] hover:border-slate-200"
    )}>
       <div className={cn(
         "absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none",
         isBlue ? "bg-gradient-to-br from-sky-50 to-transparent" : "bg-gradient-to-br from-slate-50 to-transparent"
       )} />
       <div className={cn(
         "flex items-center gap-2 text-[9px] md:text-[10px] font-bold uppercase tracking-[0.2em] mb-2 md:mb-3 relative z-10",
         isBlue ? "text-sky-600" : "text-slate-500"
       )}>
          {icon} {label}
       </div>
       <div className={cn(
         "text-sm md:text-base font-black font-mono tracking-tight relative z-10",
         isBlue ? "text-sky-900" : "text-slate-900"
       )}>{value}</div>
    </div>
  )
}

const BentoCell = ({ label, value, suffix, color }: { label: string, value: string | number, suffix?: string, color: PillColor }) => {
  const isGreen = color === 'green';
  const isRed = color === 'red';
  
  return (
    <div className={cn(
        "flex flex-col p-4 md:p-5 rounded-2xl border transition-all duration-500 group relative overflow-hidden",
        isGreen ? "bg-white border-teal-100 shadow-[0_4px_20px_-4px_rgba(20,184,166,0.1)] hover:shadow-[0_8px_30px_-6px_rgba(20,184,166,0.15)] hover:border-teal-200" :
        isRed ? "bg-white border-rose-100 shadow-[0_4px_20px_-4px_rgba(244,63,94,0.1)] hover:shadow-[0_8px_30px_-6px_rgba(244,63,94,0.15)] hover:border-rose-200" :
        "bg-white border-slate-100 shadow-[0_4px_20px_-4px_rgba(15,23,42,0.05)] hover:shadow-[0_8px_30px_-6px_rgba(15,23,42,0.08)] hover:border-slate-200"
    )}>
      <div className={cn(
        "absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none",
        isGreen ? "bg-gradient-to-br from-teal-50/50 to-transparent" : 
        isRed ? "bg-gradient-to-br from-rose-50/50 to-transparent" : 
        "bg-gradient-to-br from-slate-50 to-transparent"
      )} />
      <span className="text-[9px] md:text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500 mb-1.5 md:mb-2 relative z-10">{label}</span>
      <div className="flex items-baseline gap-1.5 font-mono relative z-10">
        <span className={cn(
          "text-sm md:text-base font-black tracking-tight",
          isGreen ? "text-teal-700" : isRed ? "text-rose-700" : "text-slate-900"
        )}>
          {value}
        </span>
        {suffix && (
          <span className={cn(
            "text-[9px] md:text-[10px] font-bold tracking-[0.2em]",
            isGreen ? "text-teal-600" : "text-rose-600"
          )}>
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
};


// ---------------------------------------------------------
// Main Export Component
// ---------------------------------------------------------
const EMPTY_ARRAY: any[] = []

export const GlobalMarketStatus = memo(function GlobalMarketStatus() {
    const { marketData } = useDashboardData()
    const activeSymbol = useSettingsStore(s => s.symbol) || 'NIFTY'
    const settingsRange = useSettingsStore(s => s.range) || 10
    const historicalDate = useSettingsStore(s => s.historicalDate)
    const activeSubTab = useSettingsStore(s => s.subTab) || 'Top Volume'
    const strikeGap = CONFIG.STRIKE_STEP?.[activeSymbol as keyof typeof CONFIG.STRIKE_STEP] || 50

    const [isExpanded, setIsExpanded] = useState(() => {
        if (typeof window !== 'undefined') {
            const symbolKey = `market_status_expanded_${activeSymbol || 'default'}`;
            const stored = localStorage.getItem(symbolKey);
            if (stored !== null) return stored !== 'false';
            return localStorage.getItem('market_status_expanded') !== 'false';
        }
        return true;
    });

    useEffect(() => {
        if (!activeSymbol) return;
        const stored = localStorage.getItem(`market_status_expanded_${activeSymbol}`);
        if (stored !== null) {
            setIsExpanded(stored !== 'false');
        }
    }, [activeSymbol]);

    useEffect(() => {
        if (!activeSymbol) return;
        localStorage.setItem(`market_status_expanded_${activeSymbol}`, isExpanded.toString());
        localStorage.setItem('market_status_expanded', isExpanded.toString());
    }, [isExpanded, activeSymbol]);

    const getTopStrikes = (options: any[], spot: number) => {
        if (!options || options.length === 0) return { top1: 0, top2: 0, top1Type: 'CE', top2Type: 'PE' }
        
        let atmStrike = 0
        if (spot > 0) {
            atmStrike = Math.round(spot / strikeGap) * strikeGap
        } else {
            atmStrike = options[Math.floor(options.length / 2)]?.strike || 0
        }

        const minStrike = atmStrike > 0 ? atmStrike - settingsRange * strikeGap : 0
        const maxStrike = atmStrike > 0 ? atmStrike + settingsRange * strikeGap : Infinity

        const filteredOptions = options.filter(o => {
            const s = o.strike || o.strikePrice || 0
            if (atmStrike > 0) {
                return s >= minStrike && s <= maxStrike
            }
            return true
        })

        const rows: any[] = []
        for (const opt of filteredOptions) {
            const strike = opt.strike || opt.strikePrice || 0
            const ceOI = opt.callOpenInterest || opt.ceOI || 0
            const peOI = opt.putOpenInterest || opt.peOI || 0
            const ceVol = opt.callVolume || opt.ceVol || 0
            const peVol = opt.putVolume || opt.peVol || 0
            const ceOiChg = opt.callOpenInterestChange || opt.ceOIChange || opt.ceOI_change || 0
            const peOiChg = opt.putOpenInterestChange || opt.peOIChange || opt.peOI_change || 0

            rows.push({
                strike, type: 'CE',
                combOI: ceOI + peOI, combVol: ceVol + peVol,
                oi: ceOI, volume: ceVol, oiChg: ceOiChg
            })
            rows.push({
                strike, type: 'PE',
                combOI: ceOI + peOI, combVol: ceVol + peVol,
                oi: peOI, volume: peVol, oiChg: peOiChg
            })
        }
        
        const getSortKey = (r: any): number => {
            switch (activeSubTab) {
                case 'Combined OI': return r.combOI
                case 'Combined Volume': return r.combVol
                case 'Top OI': return r.oi
                case 'Top Volume': return r.volume
                case 'Top Chng OI': return Math.abs(r.oiChg)
                default: return r.volume
            }
        }

        rows.sort((a,b) => getSortKey(b) - getSortKey(a))
        return {
            top1: rows[0]?.strike || 0,
            top2: rows[1]?.strike || 0,
            top1Type: rows[0]?.type || 'CE',
            top2Type: rows[1]?.type || 'PE',
        }
    }

    const activeExpiry = useSettingsStore(s => s.expiry) || ''
    const inspectionSource = useSettingsStore(s => s.inspectionSource)
    const { indexIndicators } = useIndexIndicators(activeSymbol, historicalDate)
    
    const history = useMarketStore(state => {
        if (inspectionSource === 'HistoricalSnapshot') return state.historicalHistory?.[activeSymbol] || EMPTY_ARRAY
        if (inspectionSource === 'RangeIntraday' || inspectionSource === 'RangeEOD') return state.rangeHistory?.[activeSymbol] || EMPTY_ARRAY
        return state.intradayHistory?.[activeSymbol] || EMPTY_ARRAY
    })

    const settingsBaseTime = useSettingsStore(s => s.baseTime) || '09:16'
    const baseMin = timeToMin(settingsBaseTime)
    const snap0915 = findSnapshotAtTime(history, baseMin, activeSymbol, activeExpiry)
    const options0915 = snap0915 ? extractSnapshotOptions(snap0915, activeSymbol, activeExpiry) : []
    const spot0915 = snap0915 ? extractSpotPrice(snap0915, activeSymbol) : 0

    const liveChain = marketData?.options || []
    const liveSpot = marketData?.spotPrice || 0
    const baseStrikes = getTopStrikes(options0915, spot0915)
    const liveStrikes = getTopStrikes(liveChain, liveSpot)

    const timestamp = marketData?.timestamp || 0
    const { giftNiftyLtp } = useGiftNifty(timestamp)

    const data: MarketData = {
        name: activeSymbol,
        currentValue: marketData?.spotPrice || 0,
        diff: marketData?.dayChange || 0,
        giftNifty: giftNiftyLtp || 0,
        dayLow: indexIndicators?.todayLow || 0,
        dayHigh: indexIndicators?.todayHigh || 0,
        dayOpen: indexIndicators?.todayOpen || 0,
        idx5mLow: indexIndicators?.first5Min?.low || 0,
        openT1: baseStrikes.top1,
        openT1Type: baseStrikes.top1Type,
        openT2: baseStrikes.top2,
        openT2Type: baseStrikes.top2Type,
        liveT1: liveStrikes.top1,
        liveT1Type: liveStrikes.top1Type,
        liveT2: liveStrikes.top2,
        liveT2Type: liveStrikes.top2Type,
    }

    return (
        <div className="w-full px-2 max-w-[1920px] mx-auto mb-2">
            <GeometricAccordion
                title={`Market Status (${activeSymbol})`}
                badge={<Globe size={18} />}
                isOpen={isExpanded}
                onToggle={() => setIsExpanded(!isExpanded)}
            >
                <div className="w-full relative bg-slate-50 text-slate-900 font-sans p-2 md:p-4 rounded-b-[20px] overflow-hidden">
                    {/* Global Ambient Glows (Force light mode inside this section) */}
                    <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-sky-100 rounded-full blur-[120px] pointer-events-none opacity-50" />
                    <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-teal-100 rounded-full blur-[120px] pointer-events-none opacity-50" />
                    <div className="absolute inset-0 pointer-events-none opacity-[0.02]" 
                         style={{ backgroundImage: 'linear-gradient(to right, #000 1px, transparent 1px), linear-gradient(to bottom, #000 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

                    <div className="relative z-10 w-full mx-auto">
                        <MarketGauge data={data} />
                    </div>
                </div>
            </GeometricAccordion>
        </div>
    )
})
